import instance from "../../axios";
import { getPeriodRanges } from "./periodRanges";

// Business 02: GET /api/businessDashboard/b2_dn_total_info?from=&to=
// totalFleet = managed vehicles
// totalServedVehicles + totalUnServedVehicles = fleet counted as active
// deliverynoteNetAmount = workshop revenue from that fleet
// saleNetAmount + manualInvoiceSaleNetAmount = FleetOS revenue
// invoicebalance = fleet accounts receivable

const numberFromApiValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(/,/g, "").replace(/%/g, "").trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const numberFromNamedField = (record, fieldName) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) return null;
  const matchingKey = Object.keys(record).find((key) => key.toLowerCase() === fieldName);
  return matchingKey ? numberFromApiValue(record[matchingKey]) : null;
};

const dataInsideResponse = (payload) => {
  if (payload && typeof payload === "object" && !Array.isArray(payload) && payload.data && typeof payload.data === "object") {
    return payload.data;
  }
  return payload;
};

const percentOfPartInTotal = (part, total) => {
  if (part == null || total == null || total === 0) return null;
  return (part / total) * 100;
};

const servedVehiclesPlusUnservedVehicles = (servedVehicles, unservedVehicles) => {
  if (servedVehicles == null && unservedVehicles == null) return null;
  return (servedVehicles || 0) + (unservedVehicles || 0);
};

const fleetCardsFromDeliveryNoteInfo = (payload) => {
  const info = dataInsideResponse(payload) || {};
  const managedVehicles = numberFromNamedField(info, "totalfleet");
  const servedVehicles = numberFromNamedField(info, "totalservedvehicles");
  const unservedVehicles = numberFromNamedField(info, "totalunservedvehicles");
  const activeFleet = servedVehiclesPlusUnservedVehicles(servedVehicles, unservedVehicles);
  const saleNetAmount = numberFromNamedField(info, "salenetamount");
  const manualInvoiceSaleNetAmount = numberFromNamedField(info, "manualinvoicesalenetamount");
  const fleetOsRevenue =
    saleNetAmount == null && manualInvoiceSaleNetAmount == null
      ? null
      : (saleNetAmount || 0) + (manualInvoiceSaleNetAmount || 0);
  const workshopRevenue = numberFromNamedField(info, "deliverynotenetamount");
  const netSaleAmount = saleNetAmount;
  return {
    managedVehicles,
    activeFleet,
    servedVehicles,
    unservedVehicles,
    deliveryNotes: numberFromNamedField(info, "totaldeliverynotescount"),
    fleetOsRevenue,
    workshopRevenue,
    invoiceBalance: numberFromNamedField(info, "invoicebalance"),
    activeCoverage: percentOfPartInTotal(activeFleet, managedVehicles),
    servicePenetration: percentOfPartInTotal(servedVehicles, managedVehicles),
    unservedShare: percentOfPartInTotal(unservedVehicles, managedVehicles),
    workshopYield: percentOfPartInTotal(workshopRevenue, netSaleAmount),
  };
};

const accountNameFromContract = (record) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) return "";
  const key = Object.keys(record).find((item) => item.toLowerCase() === "accountname");
  if (!key || record[key] == null) return "";
  return String(record[key]).trim();
};

const contractNumberFromContract = (record) => {
  if (!record || typeof record !== "object" || Array.isArray(record)) return "";
  const key = Object.keys(record).find((item) => {
    const name = item.toLowerCase();
    return name === "contractnumber" || name === "contractno" || name === "contract";
  });
  if (!key || record[key] == null) return "";
  return String(record[key]).trim();
};

const accountStatusFromServicePenetration = (servicePenetration) => {
  if (servicePenetration == null) return { label: "—", tone: "" };
  if (servicePenetration >= 80) return { label: "Healthy", tone: "bd-up" };
  return { label: "Watch", tone: "bd-warning" };
};

const fleetAccountsFromContractwise = (payload) => {
  const info = dataInsideResponse(payload);
  const rows = Array.isArray(info) ? info : [];
  return rows.map((row) => {
    const vehicles = numberFromNamedField(row, "totalfleet");
    const serviced = numberFromNamedField(row, "totalservedvehicles");
    const unserved = numberFromNamedField(row, "totalunservedvehicles");
    const servicePenetration = percentOfPartInTotal(serviced, vehicles);
    const accountName = accountNameFromContract(row);
    const contractNumber = contractNumberFromContract(row);
    const accountLabel = [accountName, contractNumber].filter(Boolean).join(" - ") || "—";
    return {
      account: accountLabel,
      accountName: accountName || "—",
      contractNumber,
      vehicles,
      active: servedVehiclesPlusUnservedVehicles(serviced, unserved),
      serviced,
      fleetOsRevenue: numberFromNamedField(row, "salenetamount"),
      workshopRevenue: numberFromNamedField(row, "deliverynotenetamount"),
      servicePenetration,
      status: accountStatusFromServicePenetration(servicePenetration),
    };
  }).filter((row) => row.vehicles > 0)
    .sort((left, right) => (right.fleetOsRevenue || 0) - (left.fleetOsRevenue || 0));
};

const urlWithFromAndTo = (path, range) => `${path}?from=${range.from}&to=${range.to}`;

const requestDashboard = (path, range) => {
  const url = urlWithFromAndTo(path, range);
  return instance({ url, method: "GET" }).catch((error) => {
    console.error("Business dashboard request failed", url, error?.message || error);
    return null;
  });
};

export const pointsVersusPreviousMonth = (currentValue, previousValue) => {
  if (currentValue == null || previousValue == null) return null;
  const difference = currentValue - previousValue;
  const arrow = difference > 0 ? "▲" : difference < 0 ? "▼" : "▲";
  return {
    text: `${arrow} ${Math.abs(difference).toFixed(1)} pts`,
    tone: difference > 0 ? "bd-up" : difference < 0 ? "bd-down" : "bd-muted",
  };
};

export const loadFleetBusiness = async (timezone, period = "MTD") => {
  const { current, previous, trend } = getPeriodRanges(period, timezone);
  const results = await Promise.all([
    requestDashboard("/api/businessDashboard/b2_dn_total_info", current),
    requestDashboard("/api/businessDashboard/b2_dn_total_info", previous),
    requestDashboard("/api/businessDashboard/b2_dn_total_info_contractwise", current),
    ...trend.map((range) =>
      requestDashboard("/api/businessDashboard/b2_dn_total_info", range)
    ),
  ]);
  const [currentPayload, previousPayload, contractPayload, ...trendPayloads] = results;
  return {
    current: currentPayload ? fleetCardsFromDeliveryNoteInfo(currentPayload) : null,
    previous: previousPayload ? fleetCardsFromDeliveryNoteInfo(previousPayload) : null,
    accounts: fleetAccountsFromContractwise(contractPayload),
    months: trend.map((range, index) => {
      const payload = trendPayloads[index];
      const cards = payload ? fleetCardsFromDeliveryNoteInfo(payload) : null;
      return {
        label: range.label,
        fleetOsRevenue: cards?.fleetOsRevenue ?? null,
      };
    }),
    range: { current, previous },
  };
};
